import { Injectable, inject, DestroyRef } from '@angular/core';
import { Subject, Observable, fromEvent, filter } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter as _filter } from 'lodash-es';

import {
    IHotkeyBinding,
    IHotkeyModifiers,
    HotkeyEventType
} from './hotkey.models';
import {
    createNormalizedKeyString
} from './hotkey.utils';

import { shareReplayRefCount } from 'src/app/utils/operator.utils';

/**
 * Service for managing keyboard hotkey bindings
 */
@Injectable()
export class HotkeyService {
    private readonly _document = inject(DOCUMENT);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _bindings = new Map<string, IHotkeyBinding>();
    private readonly _hotkeyTriggered$ = new Subject<IHotkeyBinding>();

    private _allDisabled = false;

    constructor() {
        this._setupEventListeners();
    }

    /**
     * Registers a hotkey binding
     * @param binding The hotkey binding to register
     * @returns The ID of the registered binding
     * @throws Error if binding is missing required fields
     */
    public register(binding: IHotkeyBinding): string {
        this._checkForDuplicates(binding);
        this._bindings.set(binding.id, binding);
        return binding.id;
    }

    /**
     * Registers an array of hotkey bindings
     * @param bindings The hotkey bindings to register
     * @returns The IDs of the registered bindings
     */
    public registerBindings(bindings: IHotkeyBinding[]): string[] {
        return bindings.map(binding => this.register(binding));
    }

    /**
     * Listen for a specific hotkey by ID
     * @param id
     * @returns Observable of the full binding object when the hotkey is triggered
     */
    public listen$(id: string): Observable<IHotkeyBinding> {
        return this._hotkeyTriggered$.pipe(
            filter(binding => binding.id === id),
            shareReplayRefCount()
        );
    }

    /**
     * Listen for all hotkeys
     * @returns Observable of the full binding object when any hotkey is triggered
     */
    public listenAll$(): Observable<IHotkeyBinding> {
        return this._hotkeyTriggered$.asObservable().pipe(
            shareReplayRefCount()
        );
    }

    /**
     * Disable hotkeys by IDs
     * @param ids Array of IDs to disable
     */
    public disableHotkeysWithIds(ids: string[]): void {
        for (const id of ids) {
            const binding = this._bindings.get(id);
            if (binding) {
                binding.enabled = false;
            }
        }
    }

    /**
     * Enable hotkeys by IDs
     * @param ids Array of IDs to enable
     */
    public enableHotkeysWithIds(ids: string[]): void {
        for (const id of ids) {
            const binding = this._bindings.get(id);
            if (binding) {
                binding.enabled = true;
            }
        }
    }

    /** Disable all hotkeys */
    public disableAll(): void {
        this._allDisabled = true;
    }

    /** Enable all hotkeys */
    public enableAll(): void {
        this._allDisabled = false;
    }

    /**
     * Remove hotkeys by IDs
     * @param ids Array of IDs to remove
     */
    public removeHotkeysByIds(ids: string[]): void {
        for (const id of ids) {
            this.removeHotkeyById(id);
        }
    }

    /**
     * Remove a specific hotkey by ID
     * @param id
     * @returns
     */
    public removeHotkeyById(id: string): boolean {
        return this._bindings.delete(id);
    }

    /**
     * Get a specific hotkey by ID
     * @param id
     * @returns
     */
    public getBinding(id: string): IHotkeyBinding | undefined {
        return this._bindings.get(id);
    }

    /**
     * Get all hotkeys
     * @returns
     */
    public getAllBindings(): IHotkeyBinding[] {
        return Array.from(this._bindings.values());
    }

    /**
     * Check if a specific hotkey exists by ID
     * @param id
     * @returns
     */
    public hasBinding(id: string): boolean {
        return this._bindings.has(id);
    }

    /** Setup event listeners on keyboard events */
    private _setupEventListeners(): void {
        // Listen to keydown and keyup events
        const keydown$ = fromEvent<KeyboardEvent>(this._document, 'keydown');
        const keyup$ = fromEvent<KeyboardEvent>(this._document, 'keyup');

        // Handle keydown events
        keydown$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(event => this._handleKeyboardEvent(event, HotkeyEventType.KEYDOWN));

        // Handle keyup events
        keyup$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(event => this._handleKeyboardEvent(event, HotkeyEventType.KEYUP));
    }

    /**
     * Keyboard event handler
     * @param event The keyboard event
     * @param eventType The type of keyboard event (keydown/keyup)
     */
    // eslint-disable-next-line complexity
    private _handleKeyboardEvent(event: KeyboardEvent, eventType: HotkeyEventType): void {
        if (this._allDisabled) {
            return;
        }

        // Skip if key is not available (can happen with some keypress events)
        if (!event.key) {
            return;
        }

        // Create normalized key string from event for comparison
        // Extract modifiers from event to build the normalized key string
        // Only include modifiers that are actually pressed (true)
        const eventModifiers: IHotkeyModifiers = {};
        if (event.shiftKey) {
            eventModifiers.shift = true;
        }
        if (event.ctrlKey) {
            eventModifiers.ctrl = true;
        }
        if (event.altKey) {
            eventModifiers.alt = true;
        }
        if (event.metaKey) {
            eventModifiers.meta = true;
        }
        const eventNormalizedKey = createNormalizedKeyString(event.key, eventModifiers);

        // Find all bindings that match this event
        for (const binding of this._bindings.values()) {
            if (!binding.enabled) {
                continue;
            }

            if (binding.eventType !== eventType) {
                continue;
            }

            // Compare normalized keys directly (includes key + modifiers)
            if (binding.normalizedKey !== eventNormalizedKey) {
                continue;
            }

            // Match found - emit the full binding object
            this._hotkeyTriggered$.next(binding);
        }
    }

    /**
     * Check for duplicate hotkey bindings
     * @param newBinding
     */
    private _checkForDuplicates(newBinding: IHotkeyBinding): void {
        const duplicates = _filter(
            Array.from(this._bindings.values()),
            existingBinding =>
                existingBinding.normalizedKey === newBinding.normalizedKey &&
                existingBinding.eventType === newBinding.eventType
        );

        if (duplicates.length > 0) {
            const duplicateIds = duplicates.map(b => b.id).join(', ');
            const keyDisplay = this._formatKeyForDisplay(newBinding);
            console.warn(
                `[HotkeyService] Multiple hotkeys assigned to the same key combination "${keyDisplay}" (event: ${newBinding.eventType}). ` +
                `Existing binding IDs: ${duplicateIds}. New binding ID: ${newBinding.id}`
            );
        }
    }

    /**
     * Format key for display in warnings
     * @param binding The binding to format
     * @returns A human-readable string representation of the key combination
     */
    // eslint-disable-next-line complexity
    private _formatKeyForDisplay(binding: IHotkeyBinding): string {
        const parts: string[] = [];

        if (binding.modifiers?.shift) {
            parts.push('Shift');
        }
        if (binding.modifiers?.ctrl) {
            parts.push('Ctrl');
        }
        if (binding.modifiers?.alt) {
            parts.push('Alt');
        }
        if (binding.modifiers?.meta) {
            parts.push('Meta');
        }

        parts.push(binding.key);

        return parts.join(' + ');
    }
}
